import styled from 'styled-components';
import { Button } from '../../../components/Button';
import { Camera } from '../../cameras/api/types';
import { ProgressBar } from './ProgressBar';

interface SetupData {
  quality: string;
  duration: number;
  includeAudio: boolean;
  scheduleHours: number[];
  scheduleDays: string[];
}

interface ConfigurationStepProps {
  selectedCamera: Camera | null;
  setupData: SetupData;
  cameraSetupComplete: boolean;
  isConfiguringRtsp: boolean;
  rtspConfigProgress: number;
  onInputChange: (field: string, value: string | number | boolean | number[] | string[]) => void;
  onRtspConfiguration: () => void;
}

export const ConfigurationStep = ({
  selectedCamera,
  setupData,
  cameraSetupComplete,
  isConfiguringRtsp,
  rtspConfigProgress,
  onInputChange,
  onRtspConfiguration
}: ConfigurationStepProps) => {
  const getProgressSubtext = (progress: number) => {
    if (progress < 40) return 'Sending configuration to camera...';
    if (progress < 80) return 'Setting up RTSP schedule...';
    return 'Completing configuration...';
  };

  return (
    <>


      <Form onSubmit={(e) => e.preventDefault()}>
        <Section>
          <SectionTitle>Configuration</SectionTitle>
          
          <FormGroup>
            <Label htmlFor="quality">Video Quality</Label>
            <QualityGrid>
              <QualityOption
                isSelected={setupData.quality === 'SD'}
                isAvailable={true}
                onClick={() => onInputChange('quality', 'SD')}
              >
                <QualityTitle>SD Quality</QualityTitle>
                <QualityDescription>Standard Definition - Available Now</QualityDescription>
              </QualityOption>
              <QualityOption
                isSelected={setupData.quality === 'HD'}
                isAvailable={false}
                onClick={() => {}} // Disabled for now
              >
                <QualityTitle>HD Quality</QualityTitle>
                <QualityDescription>High Definition - Coming Soon</QualityDescription>
              </QualityOption>
            </QualityGrid>
            <HelperText>
              Choose your preferred video quality. HD quality will be available soon.
            </HelperText>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="duration">Stream Duration</Label>
            <Select
              id="duration"
              value={setupData.duration.toString()}
              onChange={(e) => onInputChange('duration', parseInt(e.target.value))}
              required
            >
              <option value="15">15 seconds</option>
              <option value="30">30 seconds</option>
              <option value="60">60 seconds</option>
            </Select>
            <HelperText>
              Choose how long each livestream recording should be.
            </HelperText>
          </FormGroup>

          <FormGroup>
            <Label>
              <input
                type="checkbox"
                checked={setupData.includeAudio}
                onChange={(e) => onInputChange('includeAudio', e.target.checked)}
                style={{ marginRight: '0.5rem' }}
              />
              Include Audio
            </Label>
            <HelperText>
              Enable audio recording in the livestream.
            </HelperText>
          </FormGroup>
        </Section>

        <Section>
          <SectionTitle>Schedule Configuration</SectionTitle>
          
          <FormGroup>
            <Label>Active Hours (24-hour format)</Label>
            <ScheduleGrid>
              {Array.from({ length: 24 }, (_, i) => (
                <HourButton
                  key={i}
                  type="button"
                  isSelected={setupData.scheduleHours.includes(i)}
                  onClick={(e) => {
                    e.preventDefault();
                    const hours = setupData.scheduleHours.includes(i)
                      ? setupData.scheduleHours.filter(h => h !== i)
                      : [...setupData.scheduleHours, i].sort((a, b) => a - b);
                    onInputChange('scheduleHours', hours);
                  }}
                >
                  {i.toString().padStart(2, '0')}:00
                </HourButton>
              ))}
            </ScheduleGrid>
            <HelperText>
              Select the hours when livestreaming should be active.
            </HelperText>
          </FormGroup>

          <FormGroup>
            <Label>Active Days</Label>
            <DaysGrid>
              {['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'].map((day) => (
                <DayButton
                  key={day}
                  type="button"
                  isSelected={setupData.scheduleDays.includes(day)}
                  onClick={() => {
                    const days = setupData.scheduleDays.includes(day)
                      ? setupData.scheduleDays.filter(d => d !== day)
                      : [...setupData.scheduleDays, day];
                    onInputChange('scheduleDays', days);
                  }}
                >
                  {day.slice(0, 3)}
                </DayButton>
              ))}
            </DaysGrid>
            <HelperText>
              Select the days when livestreaming should be active.
            </HelperText>
          </FormGroup>
        </Section>

        {isConfiguringRtsp && (
          <ProgressBar
            progress={rtspConfigProgress}
            title="Configuring livestream settings..."
            subtitle={getProgressSubtext(rtspConfigProgress)}
          />
        )}

        <FeatureHighlight>
          <FeatureTitle>🎥 Your Livestream Configuration:</FeatureTitle>
          <FeatureList>
            <FeatureItem>• Camera: {selectedCamera?.nicknameInDatabase}</FeatureItem>
            <FeatureItem>• Quality: {setupData.quality} ({setupData.quality === 'SD' ? 'Standard Definition' : 'High Definition'})</FeatureItem>
            <FeatureItem>• Duration: {setupData.duration} seconds per stream</FeatureItem>
            <FeatureItem>• Audio: {setupData.includeAudio ? 'Included' : 'Video only'}</FeatureItem>
            <FeatureItem>• Schedule: {setupData.scheduleHours.length} hours across {setupData.scheduleDays.length} days</FeatureItem>
          </FeatureList>
        </FeatureHighlight>

        <ButtonsContainer>
          <SubmitButton 
            onClick={onRtspConfiguration}
            isLoading={isConfiguringRtsp}
            disabled={isConfiguringRtsp}
          >
            {isConfiguringRtsp ? 'Configuring Livestream...' : 'Configure Livestream Settings'}
          </SubmitButton>
        </ButtonsContainer>
      </Form>
    </>
  );
};

// Styled Components
const CameraSetupSuccess = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.green900}15, ${({ theme }) => theme.colors.green900}25);
  border: 1px solid ${({ theme }) => theme.colors.green900}40;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.green900};
  font-weight: 600;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Section = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.gray500}30;
  border-radius: 0.75rem;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid ${({ theme }) => theme.colors.primary}20;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
  margin-bottom: 0.5rem;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.gray500}50;
  border-radius: 0.5rem;
  font-size: 0.9rem;
  background: white;
  color: ${({ theme }) => theme.colors.black};
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}15;
  }
`;

const HelperText = styled.p`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.gray900};
  margin-top: 0.5rem;
  margin-bottom: 0;
  opacity: 0.8;
`;

const QualityGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const QualityOption = styled.div<{ isSelected: boolean; isAvailable: boolean }>`
  padding: 1.5rem;
  border: 2px solid ${({ theme, isSelected, isAvailable }) => 
    !isAvailable 
      ? theme.colors.gray500 + '30'
      : isSelected 
        ? theme.colors.primary 
        : theme.colors.gray500 + '30'};
  border-radius: 0.75rem;
  background: ${({ theme, isSelected, isAvailable }) => 
    !isAvailable
      ? theme.colors.gray + '50'
      : isSelected 
        ? `linear-gradient(135deg, ${theme.colors.primary}08, ${theme.colors.primary}15)`
        : theme.colors.white};
  cursor: ${({ isAvailable }) => isAvailable ? 'pointer' : 'not-allowed'};
  transition: all 0.2s ease;
  text-align: center;
  opacity: ${({ isAvailable }) => isAvailable ? 1 : 0.6};
  
  &:hover {
    ${({ isAvailable, theme }) => isAvailable && `
      border-color: ${theme.colors.primary};
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    `}
  }
`;

const QualityTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
  margin-bottom: 0.5rem;
`;

const QualityDescription = styled.p`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.gray900};
  margin: 0;
`;

const ScheduleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 0.5rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(70px, 1fr));
    gap: 0.4rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(auto-fill, minmax(60px, 1fr));
    gap: 0.3rem;
  }
`;

const HourButton = styled.button<{ isSelected: boolean }>`
  padding: 0.5rem;
  border: 2px solid ${({ theme, isSelected }) => 
    isSelected ? theme.colors.primary : theme.colors.gray500 + '30'};
  border-radius: 0.5rem;
  background: ${({ theme, isSelected }) => 
    isSelected 
      ? theme.colors.primary
      : theme.colors.white};
  color: ${({ theme, isSelected }) => 
    isSelected ? theme.colors.white : theme.colors.black};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.85rem;
  min-height: 40px;
  
  @media (max-width: 768px) {
    padding: 0.4rem;
    font-size: 0.8rem;
    min-height: 36px;
  }
  
  @media (max-width: 480px) {
    padding: 0.3rem;
    font-size: 0.75rem;
    min-height: 32px;
  }
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme, isSelected }) => 
      isSelected 
        ? theme.colors.primary + 'CC'
        : theme.colors.primary + '10'};
  }
`;

const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.5rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(7, 1fr);
    gap: 0.3rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(7, 1fr);
    gap: 0.2rem;
  }
`;

const DayButton = styled.button<{ isSelected: boolean }>`
  padding: 0.75rem 0.5rem;
  border: 2px solid ${({ theme, isSelected }) => 
    isSelected ? theme.colors.primary : theme.colors.gray500 + '30'};
  border-radius: 0.5rem;
  background: ${({ theme, isSelected }) => 
    isSelected 
      ? theme.colors.primary
      : theme.colors.white};
  color: ${({ theme, isSelected }) => 
    isSelected ? theme.colors.white : theme.colors.black};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.85rem;
  font-weight: 600;
  min-height: 48px;
  
  @media (max-width: 768px) {
    padding: 0.5rem 0.2rem;
    font-size: 0.7rem;
    min-height: 44px;
  }
  
  @media (max-width: 480px) {
    padding: 0.4rem 0.1rem;
    font-size: 0.65rem;
    min-height: 40px;
  }
  
  @media (max-width: 360px) {
    padding: 0.3rem 0.05rem;
    font-size: 0.6rem;
    min-height: 36px;
  }
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme, isSelected }) => 
      isSelected 
        ? theme.colors.primary + 'CC'
        : theme.colors.primary + '10'};
  }
`;

const FeatureHighlight = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}08, ${({ theme }) => theme.colors.primary}15);
  border: 1px solid ${({ theme }) => theme.colors.primary}25;
  border-radius: 0.75rem;
  padding: 1.5rem;
`;

const FeatureTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
  margin-bottom: 1rem;
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FeatureItem = styled.p`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.black};
  margin: 0;
  line-height: 1.4;
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  margin-top: 1.5rem;
  
  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: center;
  }
`;

const SubmitButton = styled(Button)`
  min-width: 200px;
`;
